import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';
import { useTranscriptStore } from '@/store';
import type { Speaker } from '@/types';

interface SpeakerFilterProps {
  speakers: Speaker[];
}

export function SpeakerFilter({ speakers }: SpeakerFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectedSpeakers = useTranscriptStore((state) => state.filters.selectedSpeakers);
  const toggleSpeaker = useTranscriptStore((state) => state.toggleSpeaker);
  const setSelectedSpeakers = useTranscriptStore((state) => state.setSelectedSpeakers);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSelectedLabel = () => {
    if (selectedSpeakers.length === 0) {
      return 'All Speakers';
    }
    if (selectedSpeakers.length === 1) {
      const speaker = speakers.find((s) => s.id === selectedSpeakers[0]);
      return speaker?.name ?? 'Unknown';
    }
    return `${selectedSpeakers.length} speakers`;
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-left transition-all hover:border-sky-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
      >
        <div className="flex items-center gap-2">
          {selectedSpeakers.length > 0 && (
            <div className="flex -space-x-1">
              {selectedSpeakers.slice(0, 3).map((id) => {
                const speaker = speakers.find((s) => s.id === id);
                return (
                  <div
                    key={id}
                    className="w-4 h-4 rounded-full border-2 border-white dark:border-slate-900"
                    style={{ backgroundColor: speaker?.color ?? '#6b7280' }}
                  />
                );
              })}
              {selectedSpeakers.length > 3 && (
                <div className="w-4 h-4 rounded-full bg-slate-300 dark:bg-slate-600 border-2 border-white dark:border-slate-900 text-[8px] font-medium flex items-center justify-center text-slate-600 dark:text-slate-300">
                  +{selectedSpeakers.length - 3}
                </div>
              )}
            </div>
          )}
          <span className="text-sm text-slate-900 dark:text-white">{getSelectedLabel()}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase">
              Filter Speakers
            </span>
            {selectedSpeakers.length > 0 && (
              <button
                onClick={() => setSelectedSpeakers([])}
                className="text-xs text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto p-2 space-y-1">
            {speakers.map((speaker) => {
              const isSelected = selectedSpeakers.includes(speaker.id);

              return (
                <button
                  key={speaker.id}
                  onClick={() => toggleSpeaker(speaker.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? 'bg-sky-50 dark:bg-sky-900/20'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: speaker.color }}
                  />
                  <span
                    className={`flex-1 text-sm ${
                      isSelected
                        ? 'text-sky-700 dark:text-sky-300 font-medium'
                        : 'text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {speaker.name}
                  </span>
                  {isSelected && <Check className="w-4 h-4 text-sky-600 dark:text-sky-400" />}
                </button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="flex border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setSelectedSpeakers(speakers.map((s) => s.id))}
              className="flex-1 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all"
            >
              Select All
            </button>
            <button
              onClick={() => setSelectedSpeakers([])}
              className="flex-1 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all border-l border-slate-200 dark:border-slate-700"
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
